import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Skeleton from "react-loading-skeleton";
import { useGlobalFunction } from "../../Context/GlobalFunctionContext";
import { ConvertToFloat, isValidArray } from "./common";
import { ProductStockStatusContainer } from "./productStockStatusContainer";
import { PriceTableContainerSkeleton } from "../../Skeleton/ProductListSkeleton";
import styles from "./priceStockPopup.module.css";

const GAP = 8;

export const PriceStockPopup = ({ product }) => {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const loadedFor = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  const { GetPriceTableData, getStockStatus } = useGlobalFunction();

  useEffect(() => {
    loadedFor.current = null;
    setPriceData(null);
    setStockData(null);
    setOpen(false);
  }, [product?.productId]);

  const loadData = async () => {
    if (!product?.productId || loadedFor.current === product.productId) return;
    loadedFor.current = product.productId;
    setStockLoading(true);
    try {
      const [prices, stock] = await Promise.all([
        GetPriceTableData(product.productId, product.productCode),
        getStockStatus(product.productId),
      ]);
      setPriceData(prices);
      setStockData(stock);
    } catch (error) {
      loadedFor.current = null;
      console.error("Price/stock popup error", error);
    } finally {
      setStockLoading(false);
    }
  };

  const handleToggle = () => {
    loadData();
    setPosition(null);
    setOpen((value) => !value);
  };

  const close = useCallback(() => setOpen(false), []);

  const placePanel = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const icon = trigger.getBoundingClientRect();
    const { width, height } = panel.getBoundingClientRect();
    const above = icon.top - height - GAP;
    const top = above >= GAP ? above : icon.bottom + GAP;
    const left = Math.min(Math.max(icon.right - width, GAP), window.innerWidth - width - GAP);
    setPosition({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) placePanel();
  }, [open, priceData, stockData, placePanel]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event) => {
      if (panelRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return;
      close();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, close]);

  if (!product?.productId) return null;

  return (
    <Fragment>
      <span
        ref={triggerRef}
        className={`orderstatus ${styles.trigger}`}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label="Price and stock details"
        onMouseEnter={loadData}
        onClick={handleToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleToggle();
          }
        }}
      >
        <span className="wes-dollar" />
      </span>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={panelRef}
            className={`proList-dropdown ${styles.panel}`}
            style={position ? { top: position.top, left: position.left } : { visibility: "hidden" }}
            role="dialog"
            aria-label="Pricing Details"
          >
            <div className="proList-dropdown-info">
              <h5>Pricing Details</h5>
              {(stockLoading || stockData) && (
                <div className="bg-white p-2 stock-badge">
                  <p className="m-0 fs-13 fw-semibold">
                    <span>Stock</span> Availability
                  </p>
                  {stockLoading ? (
                    <Skeleton height={16} width={110} containerClassName="d-block mt-1" style={{ display: "block" }} />
                  ) : (
                    <ProductStockStatusContainer params={stockData} />
                  )}
                </div>
              )}
              <div className="pricingDetails-dropdown">
                <div className="pricingDetails-header">
                  <div className="cost-ex">Qty</div>
                  <div className="cost-ex">Cost Ex</div>
                  <div className="cost-ex">Cost Inc</div>
                </div>
                <div className="pricingDetails-content">
                  {isValidArray(priceData) ? (
                    priceData.map((item, index) => (
                      <div
                        className={`pricingDetails-content-list${
                          item.actualQty == "your_price" ? " pricingDetails-content-listGreen" : ""
                        }`}
                        key={index}
                      >
                        <div className="price-ex">{item.qty}</div>
                        <div className="price-ex">$ {ConvertToFloat(item.priceExc)}</div>
                        <div className="price-ex">${ConvertToFloat(item.priceInc)}</div>
                      </div>
                    ))
                  ) : (
                    <PriceTableContainerSkeleton count={3} />
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </Fragment>
  );
};
